import { useEffect } from "react";

const FIELD_SELECTOR = [
  "input:not([type='hidden'])",
  "select",
  "textarea",
  "[contenteditable='true']",
].join(",");

const estaVisible = (element) => {
  if (!(element instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return style.display !== "none" && style.visibility !== "hidden" && rect.width > 0 && rect.height > 0;
};

const estaDisponible = (element) =>
  estaVisible(element) &&
  !element.matches(":disabled") &&
  element.getAttribute("aria-hidden") !== "true" &&
  element.tabIndex !== -1;

const obtenerAlcance = (element) =>
  element.closest("form") ||
  element.closest('[role="dialog"]') ||
  element.closest(".main-content") ||
  document.body;

const camposDelAlcance = (element) =>
  [...obtenerAlcance(element).querySelectorAll(FIELD_SELECTOR)].filter(estaDisponible);

const INPUT_TYPES_CON_CURSOR = ["text", "search", "tel", "url", "password"];

const enfocar = (element, posicionCursor = "end") => {
  element?.focus({ preventScroll: true });
  element?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  if (element instanceof HTMLInputElement && INPUT_TYPES_CON_CURSOR.includes(element.type)) {
    const posicion = posicionCursor === "start" ? 0 : element.value.length;
    element.setSelectionRange(posicion, posicion);
  }
};

const enfocarPorOrden = (actual, direccion) => {
  const campos = camposDelAlcance(actual);
  const index = campos.indexOf(actual);
  const siguiente = campos[index + direccion];
  if (!siguiente) return false;
  enfocar(siguiente);
  return true;
};

const distanciaSecundaria = (actual, candidato, vertical) => {
  if (vertical) {
    if (candidato.right >= actual.left && candidato.left <= actual.right) return 0;
    return Math.min(Math.abs(candidato.left - actual.right), Math.abs(actual.left - candidato.right));
  }
  if (candidato.bottom >= actual.top && candidato.top <= actual.bottom) return 0;
  return Math.min(Math.abs(candidato.top - actual.bottom), Math.abs(actual.top - candidato.bottom));
};

const enfocarEnDireccion = (actual, key) => {
  const rectActual = actual.getBoundingClientRect();
  const centroActual = {
    x: rectActual.left + rectActual.width / 2,
    y: rectActual.top + rectActual.height / 2,
  };
  const vertical = key === "ArrowUp" || key === "ArrowDown";
  const positivo = key === "ArrowRight" || key === "ArrowDown";
  const candidatos = camposDelAlcance(actual)
    .filter((campo) => campo !== actual)
    .map((campo) => {
      const rect = campo.getBoundingClientRect();
      const centro = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      const deltaPrincipal = vertical ? centro.y - centroActual.y : centro.x - centroActual.x;
      if ((positivo && deltaPrincipal <= 2) || (!positivo && deltaPrincipal >= -2)) return null;
      const principal = Math.abs(deltaPrincipal);
      const secundaria = distanciaSecundaria(rectActual, rect, vertical);
      return { campo, puntaje: principal + secundaria * 3 };
    })
    .filter(Boolean)
    .sort((a, b) => a.puntaje - b.puntaje);

  if (!candidatos.length) return false;
  const posicionCursor = key === "ArrowRight" ? "start" : "end";
  enfocar(candidatos[0].campo, posicionCursor);
  return true;
};

const puedeSalirHorizontalmente = (element, key) => {
  if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) return true;
  if (element instanceof HTMLInputElement && !INPUT_TYPES_CON_CURSOR.includes(element.type)) return true;
  const inicio = element.selectionStart;
  const fin = element.selectionEnd;
  if (inicio === null || fin === null) return true;
  return key === "ArrowLeft" ? inicio === 0 && fin === 0 : inicio === element.value.length && fin === element.value.length;
};

const puedeSalirVerticalmente = (element, key) => {
  if (!(element instanceof HTMLTextAreaElement)) return true;
  const posicion = element.selectionStart ?? 0;
  if (key === "ArrowUp") return !element.value.slice(0, posicion).includes("\n");
  return !element.value.slice(posicion).includes("\n");
};

const opcionesBuscador = (element) => {
  const buscador = element.closest(".searchable-select");
  if (!buscador) return [];
  return [...buscador.querySelectorAll(".searchable-options button:not(:disabled)")].filter(estaVisible);
};

const limpiarOpcionActiva = (opciones) => opciones.forEach((opcion) => {
  opcion.classList.remove("is-keyboard-active");
  opcion.removeAttribute("aria-selected");
});

const moverOpcionBuscador = (element, key) => {
  const opciones = opcionesBuscador(element);
  if (!opciones.length) return false;
  const actual = opciones.findIndex((opcion) => opcion.classList.contains("is-keyboard-active"));
  const siguiente = actual < 0
    ? (key === "ArrowDown" ? 0 : opciones.length - 1)
    : Math.min(Math.max(actual + (key === "ArrowDown" ? 1 : -1), 0), opciones.length - 1);
  limpiarOpcionActiva(opciones);
  opciones[siguiente].classList.add("is-keyboard-active");
  opciones[siguiente].setAttribute("aria-selected", "true");
  opciones[siguiente].scrollIntoView({ block: "nearest" });
  return true;
};

const elegirOpcionBuscador = (element) => {
  const opciones = opcionesBuscador(element);
  if (!opciones.length) return false;
  const opcion = opciones.find((item) => item.classList.contains("is-keyboard-active")) || opciones[0];
  const esMultiple = Boolean(element.closest(".multiselect-field"));
  opcion.click();
  if (!esMultiple) window.setTimeout(() => enfocarPorOrden(element, 1), 0);
  return true;
};

export const useFormKeyboardNavigation = () => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.matches(FIELD_SELECTOR)) return;

      const enBuscadorCodigo = Boolean(target.closest(".code-search"));
      const esSelect = target instanceof HTMLSelectElement;
      const esBuscador = Boolean(target.closest(".searchable-select"));

      if ((event.key === "ArrowDown" || event.key === "ArrowUp") && esBuscador) {
        if (moverOpcionBuscador(target, event.key)) {
          event.preventDefault();
          return;
        }
      }

      if (event.key === "Enter") {
        if (esBuscador && elegirOpcionBuscador(target)) {
          event.preventDefault();
          return;
        }
        if (enBuscadorCodigo) return;
        if (target instanceof HTMLTextAreaElement && event.shiftKey) return;
        if (enfocarPorOrden(target, event.shiftKey ? -1 : 1)) event.preventDefault();
        return;
      }

      if (event.key === "Tab" && esBuscador) {
        if (enfocarPorOrden(target, event.shiftKey ? -1 : 1)) event.preventDefault();
        return;
      }

      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      if (esSelect && (event.key === "ArrowUp" || event.key === "ArrowDown")) return;
      if ((event.key === "ArrowLeft" || event.key === "ArrowRight") && !puedeSalirHorizontalmente(target, event.key)) return;
      if ((event.key === "ArrowUp" || event.key === "ArrowDown") && !puedeSalirVerticalmente(target, event.key)) return;
      if (enfocarEnDireccion(target, event.key)) event.preventDefault();
    };

    const handleInput = (event) => {
      const target = event.target;
      if (target instanceof HTMLElement) limpiarOpcionActiva(opcionesBuscador(target));
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("input", handleInput, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("input", handleInput, true);
    };
  }, []);
};
